/**
 * Which row the master pane is pointed at.
 *
 * **Promoted from `pages/plan-library/lib` on 17 September 2026, on its second consumer**: the
 * exercise library's table selects and walks by the same rule, and a page may not import another
 * page's internals. Nothing about the hook changed in the move — the plan library's own canon is
 * where it was decided, and it is quoted below because it is still the reason each line is there.
 *
 * KANON `Programy v2 - D3 (0.8)` selects on hover, and the owner's decision of 3 September 2026
 * adds the half the specimen cannot show: **the selection STAYS on the last row pointed at**,
 * including after the pointer leaves the list entirely. So this is state, not a `:hover` rule — a
 * CSS hover would empty the inspector the moment the trainer reached for the scrollbar.
 *
 * ── What the trainer POINTED at and what the list SHOWS are two things ───────────────────────
 * `pointedKey` is the trainer's own last answer and is remembered across a search that hides it;
 * `selectedKey` is what is drawn, and falls back to the first visible row when a filter takes the
 * pointed-at row off the list. Keeping them apart is what makes clearing a search put the trainer
 * back on the row they chose rather than on whoever the fallback picked in the meantime. It is
 * `pages/client-roster`'s `previewSelection` rule, arriving on the screen the same canon governs.
 *
 * ── The keys are the LIST's, so a scope switch resets by construction ────────────────────────
 * Switching to Plany klientów replaces the whole key set, so the remembered key is not in it and
 * the fallback answers with the first row of the new list — which is exactly what the owner asked
 * for ("przełączenie zeruje wybór na pierwszy wiersz") without a second piece of state to keep in
 * step with the scope.
 *
 * ── Moving with the keyboard is a FOCUS move, not a second mechanism ─────────────────────────
 * `step` answers which key is N rows away; the component focuses that row (its link on the plan
 * library, the row itself on the exercise table, which has no link yet), and the focus
 * lands on `onFocusCapture`, which calls `point`. One path into the selection whether the trainer
 * used a pointer or a keyboard, and `Enter` then follows the anchor that already has focus. A hook
 * that moved focus itself would have to hold DOM refs for rows it never renders.
 *
 * `step` CLAMPS rather than stopping at `null`, which is what lets one function answer the arrows,
 * `Home`/`End` (±Infinity) and `PageUp`/`PageDown` (±a screenful) alike. "There is nowhere to go"
 * is then the caller's own comparison — the key it got back is the one focus is already on — and
 * the caller leaves the event alone so the browser's scroll still works at the ends.
 */

import { useState } from 'react';

export interface PointedRow {
  /** The row the list draws as selected, or `null` when there are no rows at all. */
  readonly selectedKey: string | null;
  /** The trainer pointed here, or focus landed here. */
  readonly point: (key: string) => void;
  /**
   * The key `steps` rows from the selection, clamped to the ends. `0` is the selection itself,
   * which is what the FIRST arrow press from the region answers with — a list entered from its
   * own tab stop must start where the pane is already pointed, not one row past it.
   *
   * `null` only when the list has no rows.
   */
  readonly step: (steps: number) => string | null;
}

export function usePointedRow(keys: readonly string[]): PointedRow {
  const [pointedKey, setPointedKey] = useState<string | null>(null);

  const selectedKey =
    pointedKey !== null && keys.includes(pointedKey) ? pointedKey : (keys[0] ?? null);

  return {
    selectedKey,
    point: setPointedKey,
    step: (steps) => {
      if (selectedKey === null) return null;
      // `indexOf` on the resolved selection rather than on `pointedKey`: after a filter the two
      // differ, and stepping from a row that is not on screen would jump the trainer somewhere
      // they cannot see.
      const at = keys.indexOf(selectedKey);
      // Both ends CLAMP rather than wrap. A list that wraps from the last row to the first reads as
      // having scrolled to the top on its own, and there is no second list here for a wrap to mean
      // anything else. Clamping is also what makes `±Infinity` the first and last row.
      const target = Math.min(Math.max(at + steps, 0), keys.length - 1);
      return keys[target] ?? null;
    },
  };
}
